import React, { useEffect, useReducer, useState } from 'react'
import { Control, Controller, UseFormSetValue } from 'react-hook-form'
import { InferType } from 'yup'
import Compressor from 'compressorjs'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { societyCreatePostSchema } from '../../../forms/schemas/society_create_post'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { Button } from '../../common/Button'
import { Dropdown } from '../../common/Dropdown'
import { Icon, IconChoices } from '../../common/Icon'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { imageUploadReducer } from '../../../reducers/image-upload'
import { ProcessedImage, ProcessedMedia } from '../../../types/image-upload'
import { convertBase64 } from '../../../api/helpers'
import { ImageActions } from '../../../actions/image-upload'
import { theme } from '../../../theme/theme'
import { PostThumbnails } from './PostThumbnails'
import { formatFileSize } from '../../../helpers/number'
import { documentUploadReducer } from '../../../reducers/document-upload'
import { DocumentActions } from '../../../actions/document-upload'
import {
  ProcessedDocument,
  ProcessedFile,
} from '../../../types/document-upload'
import { EventPlanner } from './EventPlanner'
import { Thumbnail } from './Thumbnail'
import { PollEditor } from './PollEditor'
import { toBase64 } from '../../common/DropZone/helpers'
import { ImportantCheckbox } from '../ImportantCheckbox'
import { PinnedCheckbox } from '../PinnedCheckbox'
import { FormatTextPopover } from '../CreateEditPostModal/CreateEditPostModalContent/FormatTextPopover'
import { isImageType } from '../../../helpers/types'
import { IPostModel } from '../../../interfaces/models/posts.interfaces'
import {
  createDocument,
  documentQueries,
  downloadDocumentFile,
} from '../../../api/documents'
import { createMedia, mediaQueries } from '../../../api/media'
import { IDocumentModel } from '../../../interfaces/models/documents.interfaces'
import { NDocuments } from '../../../interfaces/services/documents.interfaces'
import { eventQueries } from '../../../api/events'
import { pollQueries } from '../../../api/polls'
import { orderedIds, PostAttachments, seedAttachments } from './attachmentSeed'

interface AttachmentsContainerProps {
  control: Control<InferType<typeof societyCreatePostSchema>>
  setValue: UseFormSetValue<InferType<typeof societyCreatePostSchema>>
  setLoadingAttachment: React.Dispatch<React.SetStateAction<boolean>>
  watchSociety: string
  watchEventsIds: (string | undefined)[] | undefined
  watchPollIds: (string | undefined)[] | undefined
  watchRole: string
  watchIsAlert: boolean | undefined
  canPin: boolean
  watchPinned: boolean | undefined
  post: IPostModel | undefined
  attachments?: PostAttachments
}

export const AttachmentsContainer = ({
  control,
  setValue,
  setLoadingAttachment,
  watchSociety,
  watchEventsIds,
  watchPollIds,
  watchRole,
  watchIsAlert,
  canPin,
  watchPinned,
  post,
  attachments,
}: AttachmentsContainerProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const client = useQueryClient()
  const { setToastNotification } = useToastNotifications()
  const { data: events } = useQueries({
    queries:
      watchEventsIds?.map((eventId) => eventQueries.getEvent(eventId)) || [],
    combine: (responses) => {
      return {
        data: responses.flatMap((query) => query.data || []),
      }
    },
  })

  const { data: polls } = useQueries({
    queries: watchPollIds?.map((pollId) => pollQueries.card(pollId)) || [],
    combine: (responses) => {
      return {
        data: responses.flatMap((query) => query.data || []),
      }
    },
  })

  const [seed] = useState(() => seedAttachments(post, attachments))
  const [imagesState, imagesDispatch] = useReducer(imageUploadReducer, {
    images: seed.images,
  })

  const [documentsState, documentsDispatch] = useReducer(
    documentUploadReducer,
    { documents: seed.documents }
  )

  const [displayEvent, setDisplayEvent] = useState<boolean>(false)
  const [displayPoll, setDisplayPoll] = useState<boolean>(false)

  const [editPollId, setEditPollId] = useState<undefined | string>(undefined)
  const [editEventId, setEditEventId] = useState<undefined | string>(undefined)

  const maxImageUploadLimit = 10
  const maxDocumentUploadLimit = 10

  useEffect(() => {
    setValue(
      'media',
      orderedIds(
        post?.media,
        seed.preservedMedia,
        imagesState.images.map((_image) => _image.id)
      ),
      { shouldValidate: true, shouldDirty: true }
    )
  }, [imagesState, post?.media, seed, setValue])

  useEffect(() => {
    setValue(
      'documentsIds',
      orderedIds(
        post?.documentsIds,
        seed.preservedDocuments,
        documentsState.documents.map((_document) => _document.id)
      ),
      { shouldValidate: true, shouldDirty: true }
    )
  }, [documentsState, post?.documentsIds, seed, setValue])

  const checkImageUploading = (images: ProcessedMedia[]): boolean => {
    return images.map((image) => image.uploading).includes(true)
  }

  const checkDocumentUploading = (documents: ProcessedFile[]): boolean => {
    return documents.map((document) => document.uploading).includes(true)
  }

  const imageUploading = checkImageUploading(imagesState.images)

  const documentUploading = checkDocumentUploading(documentsState.documents)

  useEffect(() => {
    setLoadingAttachment(imageUploading || documentUploading)
  }, [documentUploading, imageUploading, setLoadingAttachment])

  const hasImageLimitExceeded = (imagesLength: number): boolean => {
    return (
      imagesLength > maxImageUploadLimit ||
      imagesState.images.length + imagesLength > maxImageUploadLimit
    )
  }

  const hasDocumentLimitExceeded = (documentLength: number): boolean => {
    return (
      documentLength > maxDocumentUploadLimit ||
      documentsState.documents.length + documentLength > maxDocumentUploadLimit
    )
  }

  const uploadImage = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const images = e.target.files && e.target.files
    if (!images) {
      return
    }
    if (hasImageLimitExceeded(images.length)) {
      setToastNotification(
        ToastType.DANGER,
        translate('chatUpload.flashMessage.uploadImageLimitReached')
      )
    } else {
      Array.from(images).forEach((image: File) => {
        if (!isImageType(image.type)) {
          setToastNotification(
            ToastType.DANGER,
            translate('uploadImage.invalidImageType')
          )
          return
        }
        // eslint-disable-next-line no-new
        new Compressor(image, {
          quality: 0.8, // 0.6 can also be used, but its not recommended to go below.
          success: async (result) => {
            const _image: ProcessedImage = {
              uri: URL.createObjectURL(image),
              name: image.name,
              size: formatFileSize(image.size),
            }

            imagesDispatch({ type: ImageActions.ADD, image: _image })

            const base64 = await convertBase64(result)
            const { data } = (await createMedia('image', base64 as string)).data
            if (data) {
              client.setQueryData(
                mediaQueries.getMedia(data._id).queryKey,
                () => {
                  return data
                }
              )
              imagesDispatch({
                type: ImageActions.SET_UPLOADED,
                mediaId: data._id as string,
                image: _image,
              })
            }
          },
        })
      })
    }
    e.target.value = ''
  }

  const uploadDocument = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const documents = e.target.files && e.target.files
    if (!documents) {
      return
    }
    if (hasDocumentLimitExceeded(documents.length)) {
      setToastNotification(
        ToastType.DANGER,
        translate('chatUpload.flashMessage.uploadDocumentLimitReached')
      )
    } else {
      Array.from(documents).map(async (document: File): Promise<void> => {
        const { name } = document
        const _document: ProcessedDocument = {
          uri: URL.createObjectURL(document),
          name,
          size: formatFileSize(document.size),
          icon: IconChoices.DOCUMENT,
        }
        documentsDispatch({ type: DocumentActions.ADD, document: _document })

        const { base64 } = await toBase64(document)
        const variables = {
          data: base64,
          type: document.name.split('.').pop() as IDocumentModel['type'],
          filename: _document.name,
          name: _document.name,
          societyId: watchSociety,
          section: 'feed',
        } satisfies NDocuments.NCreate.IRequestBody

        const { data } = (await createDocument(variables)).data

        if (data) {
          client.setQueryData(
            documentQueries.getDocument(data._id).queryKey,
            () => {
              return data
            }
          )
          documentsDispatch({
            type: DocumentActions.SET_UPLOADED,
            id: data._id as string,
            document: _document,
          })
        }
      })
    }
    e.target.value = ''
  }

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    uploadImage(e)
  }

  const onDocumentChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    uploadDocument(e)
  }

  const onImageRemove = (id: string): void => {
    imagesDispatch({ type: ImageActions.REMOVE, id })
  }

  const onDocumentRemove = (id: string): void => {
    documentsDispatch({
      type: DocumentActions.REMOVE,
      id,
    })
  }

  const onEventRemove = (id: string): void => {
    setValue(
      'eventsIds',
      watchEventsIds?.filter((_id) => _id !== id),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    )
    setDisplayEvent(false)
  }

  const onPollRemove = (id: string): void => {
    setValue(
      'pollIds',
      watchPollIds?.filter((_id) => _id !== id),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    )
    setDisplayPoll(false)
  }

  const onPollThumbnailClick = (id: string): void => {
    setEditPollId(id)
    setDisplayPoll(true)
  }

  const onEventThumbnailClick = (id: string): void => {
    setEditEventId(id)
    setDisplayEvent(true)
  }

  const hasEventOrPoll =
    (watchPollIds && watchPollIds.length > 0) ||
    (watchEventsIds && watchEventsIds.length > 0)

  const dropdownOptions = [
    {
      value: 'pictures',
      label: translate('postCreateUpdateBase.attachments.pictures'),
      customContent: (
        <div className={`flex ${watchRole !== 'resident' ? 'px-4 pt-4' : ''}`}>
          <div className="flex w-full items-center">
            <label
              htmlFor="post-image-input"
              className="flex h-full w-full cursor-pointer items-center gap-3 rounded 
              p-3 hover:bg-neutral-96"
            >
              <Icon icon={IconChoices.IMAGE} />
              <span style={theme.textVariants.baseBold}>
                {translate('postCreateUpdateBase.attachments.pictures')}
              </span>
            </label>
          </div>
        </div>
      ),
    },
    // {
    //   value: 'gallery',
    //   label: translate('postCreateUpdateBase.attachments.gallery'),
    //   icon: IconChoices.IMAGES,
    //   onClick: () => null,
    // },
    ...(watchRole !== 'resident'
      ? [
          {
            value: 'document',
            label: translate('postCreateUpdateBase.attachments.document'),
            customContent: (
              <div className="flex px-4">
                <div className="flex w-full items-center">
                  <label
                    htmlFor="post-document-input"
                    className="flex h-full w-full cursor-pointer items-center 
                    gap-3 rounded p-3 hover:bg-neutral-96"
                  >
                    <Icon icon={IconChoices.DOCUMENT_2} />
                    <span style={theme.textVariants.baseBold}>
                      {translate('postCreateUpdateBase.attachments.document')}
                    </span>
                  </label>
                </div>
              </div>
            ),
          },
          {
            value: 'event',
            label: translate('postCreateUpdateBase.attachments.event'),
            icon: IconChoices.CALENDAR,
            disabled: hasEventOrPoll,
            onClick: () => {
              setDisplayPoll(false)
              setDisplayEvent(true)
            },
          },
          {
            value: 'poll',
            label: translate('postCreateUpdateBase.attachments.poll'),
            icon: IconChoices.BAR_CHART,
            disabled: hasEventOrPoll,
            onClick: () => {
              setDisplayEvent(false)
              setDisplayPoll(true)
            },
          },
        ]
      : []),
  ]

  const downloadDocument = (id: string, filename: string): void => {
    downloadDocumentFile(id, filename)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <Controller
          control={control}
          name="media"
          render={() => (
            <input
              id="post-image-input"
              type="file"
              accept="image/png, image/jpg, image/jpeg"
              className="h-0 w-0 max-w-0 opacity-0"
              tabIndex={0}
              multiple
              onChange={onImageChange}
            />
          )}
        />
        <Controller
          control={control}
          name="documentsIds"
          render={() => (
            <input
              id="post-document-input"
              type="file"
              accept=".xlsx,.xls,.doc,.docx,.ppt,.pptx,.txt,.pdf"
              className="h-0 w-0 max-w-0 opacity-0"
              tabIndex={0}
              multiple
              onChange={onDocumentChange}
            />
          )}
        />
        <div className="flex flex-wrap gap-2">
          <Dropdown
            className="w-fit"
            dropdownPosition="bottom-right"
            options={dropdownOptions}
          >
            <Button
              label={translate('postCreateUpdateBase.media.attach')}
              iconPlacement="right"
              icon={IconChoices.PLUS_SIGN}
            />
          </Dropdown>
          <ImportantCheckbox
            control={control}
            watchRole={watchRole}
            watchIsAlert={!!watchIsAlert}
          />
          {canPin && (
            <PinnedCheckbox control={control} watchPinned={!!watchPinned} />
          )}
          <FormatTextPopover />
        </div>
      </div>
      {imagesState.images.length > 0 && (
        <PostThumbnails header={translate('postCreateUpdateBase.media.images')}>
          {imagesState.images.map((image, index) => (
            <Thumbnail
              key={`${image.uri}${image.id}`}
              id={image.id as string}
              uri={image.uri}
              title={image.name || `Image-${index}`}
              description={image.size || 'Unknown file size'}
              index={index}
              onThumbnailRemove={onImageRemove}
              onThumbnailClick={() =>
                window.open(image.uri, '_blank', 'noreferrer noopener')
              }
              loading={image.uploading}
            />
          ))}
        </PostThumbnails>
      )}
      {documentsState.documents.length > 0 && (
        <PostThumbnails
          header={translate('postCreateUpdateBase.media.documents')}
        >
          {documentsState.documents.map((doc, index) => (
            <Thumbnail
              key={`${doc.id}${doc.name}`}
              id={doc.id as string}
              icon={doc.icon}
              title={doc.name || `File-${index}`}
              description={doc.size || 'Unknown file size'}
              index={index}
              onThumbnailClick={() =>
                downloadDocument(doc.id as string, doc.name)
              }
              onThumbnailRemove={onDocumentRemove}
              loading={doc.uploading}
            />
          ))}
        </PostThumbnails>
      )}
      {events && events.length > 0 && (
        <PostThumbnails
          header={translate('postCreateUpdateBase.attachments.event')}
        >
          {events.map((event, index) => {
            return (
              <Thumbnail
                key={`${event._id}${event.title}`}
                id={event._id as string}
                icon={IconChoices.CALENDAR}
                title={event.title}
                description={event.location as string}
                index={index}
                onThumbnailRemove={onEventRemove}
                onThumbnailClick={onEventThumbnailClick}
              />
            )
          })}
        </PostThumbnails>
      )}
      {polls && polls.length > 0 && (
        <PostThumbnails
          header={translate('postCreateUpdateBase.attachments.poll')}
        >
          {polls.map((poll, index) => {
            return (
              <Thumbnail
                key={`${poll.id}${poll.title}`}
                id={poll.id}
                icon={IconChoices.DOCUMENT_2}
                title={poll.title}
                description={translate('pollEditor.pollDescription')}
                index={index}
                onThumbnailRemove={onPollRemove}
                onThumbnailClick={onPollThumbnailClick}
              />
            )
          })}
        </PostThumbnails>
      )}
      {displayEvent && watchRole !== 'resident' && (
        <div className="flex flex-col gap-2">
          <p style={theme.textVariants.baseBold}>
            {translate('postCreateUpdateBase.attachments.event')}
          </p>
          <EventPlanner
            eventId={editEventId}
            setPostValue={setValue}
            setDisplayEvent={setDisplayEvent}
            watchSociety={watchSociety}
            watchEventsIds={watchEventsIds}
          />
        </div>
      )}
      {displayPoll && watchRole !== 'resident' && (
        <div className="flex flex-col gap-2">
          <p style={theme.textVariants.baseBold}>
            {translate('postCreateUpdateBase.attachments.poll')}
          </p>
          <PollEditor
            pollId={editPollId}
            setPostValue={setValue}
            setDisplayPoll={setDisplayPoll}
            watchSociety={watchSociety}
            watchPollIds={watchPollIds}
          />
        </div>
      )}
    </div>
  )
}
